/**
 * Status counts and the message total across every connection, shown above the
 * monitoring list.
 */
import { Box, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { EhrSyncConnection } from '@models/ehr-sync';

import { statusMessages } from '../../messages';
import { effectiveStatus } from '../shared/monitoring.util';

const messages = defineMessages({
  messagesInRetention: {
    id: 'ehrSync.list.summary.messagesInRetention',
    defaultMessage: 'Messages in retention',
  },
  failedOrMismatched: {
    id: 'ehrSync.list.summary.failedOrMismatched',
    defaultMessage: 'Failed or mismatched',
  },
});

interface SummaryCardsProps {
  integrations: EhrSyncConnection[];
}

type Tone = 'success' | 'warning' | 'error' | 'primary';

export default function SummaryCards({ integrations }: SummaryCardsProps) {
  const { formatMessage, formatNumber } = useIntl();

  // Registry problems count as failed and only there, so no connection is counted twice.
  const countBy = (status: EhrSyncConnection['status']) =>
    integrations.filter(
      (integration) => effectiveStatus(integration) === status
    ).length;

  const totalMessages = integrations.reduce(
    (sum, integration) => sum + integration.recordCount,
    0
  );

  const cards: { label: string; value: string; tone: Tone }[] = [
    {
      label: formatMessage(statusMessages.connected),
      value: formatNumber(countBy('connected')),
      tone: 'success',
    },
    {
      label: formatMessage(statusMessages.degraded),
      value: formatNumber(countBy('degraded')),
      tone: 'warning',
    },
    {
      label: formatMessage(messages.failedOrMismatched),
      value: formatNumber(countBy('failed')),
      tone: 'error',
    },
    {
      // QIE purges old messages, so this is what it still holds, not a lifetime total.
      label: formatMessage(messages.messagesInRetention),
      value: formatNumber(totalMessages, { notation: 'compact' }),
      tone: 'primary',
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 2,
        mb: 2.5,
      }}
    >
      {cards.map((card) => (
        <Box
          key={card.label}
          sx={(theme) => ({
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderTop: `3px solid ${theme.palette[card.tone].main}`,
            borderRadius: 1,
            px: 2.25,
            py: 1.75,
          })}
        >
          <Typography variant="h5" fontWeight="bold" lineHeight={1.1}>
            {card.value}
          </Typography>
          <Typography variant="overline" color="text.secondary">
            {card.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
